import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import { flattenQuery } from 'helpers/flattenQuery';
import type { FolderPath } from 'helpers/folderPaths';
import { FilterConfig } from 'hooks/useListQuery';
import { extractErrorMessage } from 'helpers/extractErrorMessage';

export interface TriggerParameter {
  name: string;
  value: string;
}

export interface Trigger {
  trigger_id: number;
  zone_id: number;
  zone_name?: string;
  // Folder support (nap-backend #1076). Zone-scoped.
  folder_id?: number | null;
  folder_path?: FolderPath | null;
  name: string;
  type: string;
  monitor_all_zones?: boolean;
  monitor_zone_ids?: number[];
  monitor_zone_names?: string[];
  node_definition_id?: number | null;
  node_definition_name?: string | null;
  from_status?: string | null;
  to_status?: string | null;
  action?: string;
  target_node_definition_id?: number | null;
  target_node_definition_name?: string | null;
  target_event_id?: number | null;
  target_event_name?: string | null;
  target_event_zone_name?: string | null;
  queue_id?: number | null;
  queue_name?: string | null;
  queue_zone_name?: string | null;
  parameters?: TriggerParameter[];
  restart_fire_mode?: 'all' | 'first' | 'last' | 'first_and_last';
  trigger_node_status_change_id?: number;
  status_change?: {
    trigger_node_status_change_id?: number;
    node_definition_id?: number | null;
    node_definition_name?: string | null;
    from_status?: string | null;
    to_status?: string | null;
    action?: string;
    target_node_definition_id?: number | null;
    target_node_definition_name?: string | null;
    target_event_id?: number | null;
    target_event_name?: string | null;
    target_event_zone_name?: string | null;
    queue_id?: number | null;
    queue_name?: string | null;
    queue_zone_name?: string | null;
    target_default_queue_id?: number | null;
    parameters?: TriggerParameter[];
    restart_fire_mode?: 'all' | 'first' | 'last' | 'first_and_last';
  };
  created_at?: string;
  updated_at?: string;
}

interface PaginationState {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

interface TriggersState {
  triggers: Trigger[];
  pagination: PaginationState;
  status: string | null;
  error: string | null;
  config: { header_key: string; label: string; header_type: string }[];
  filtersConfig: FilterConfig[];
  loading: {
    getTriggersPaginated: boolean;
  };
}

const initialState: TriggersState = {
  triggers: [],
  pagination: {
    totalItems: 0,
    totalPages: 1,
    currentPage: 1,
    pageSize: 50,
  },
  status: null,
  error: null,
  config: [],
  filtersConfig: [],
  loading: {
    getTriggersPaginated: false,
  },
};

export const getTriggersPaginated = createAsyncThunk('triggers/getTriggersPaginated', async (params: Record<string, any>) => {
  const res = await api.get('/triggers', { params: flattenQuery(params) });
  return res.data;
});

export const fetchTrigger = createAsyncThunk('triggers/fetchTrigger', async (trigger_id: string, { rejectWithValue }) => {
  try {
    const response = await api.get(`/triggers/${trigger_id}`);
    return response.data;
  } catch (e) {
    return rejectWithValue('');
  }
});

export const createTrigger = createAsyncThunk(
  'triggers/createTrigger',
  async (
    payload: {
      name: string;
      zone_id: number;
      type: string;
      monitor_zone_ids?: number[];
      node_definition_id?: number | null;
      from_status?: string | null;
      to_status?: string | null;
      action?: string;
      target_node_definition_id?: number | null;
      target_event_id?: number | null;
      queue_id?: number | null;
      parameters?: TriggerParameter[];
      folder_id: number | null;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post('/triggers', payload);
      return response.data;
    } catch (e: any) {
      return rejectWithValue(extractErrorMessage(e.response?.data, 'Failed to create trigger'));
    }
  }
);

export const updateTrigger = createAsyncThunk(
  'triggers/updateTrigger',
  async (
    payload: {
      trigger_id: string;
      name?: string;
      zone_id?: number;
      type?: string;
      monitor_zone_ids?: number[];
      node_definition_id?: number | null;
      from_status?: string | null;
      to_status?: string | null;
      action?: string;
      target_node_definition_id?: number | null;
      target_event_id?: number | null;
      queue_id?: number | null;
      parameters?: TriggerParameter[];
      folder_id: number | null;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.put(`/triggers/${payload.trigger_id}`, payload);
      return response.data;
    } catch (e: any) {
      return rejectWithValue(extractErrorMessage(e.response?.data, 'Failed to update trigger'));
    }
  }
);

export const deleteTrigger = createAsyncThunk('triggers/deleteTrigger', async (trigger_id: string, { rejectWithValue }) => {
  try {
    await api.delete(`/triggers/${trigger_id}`);
    return { trigger_id };
  } catch (e) {
    return rejectWithValue('');
  }
});

const triggersSlice = createSlice({
  name: 'triggers',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getTriggersPaginated.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.getTriggersPaginated = true;
      })
      .addCase(getTriggersPaginated.fulfilled, (state, action) => {
        state.status = 'succeeded';

        const { triggers, config, filtersConfig, pagination } = action.payload;

        state.triggers = triggers || [];
        state.config = config || [];
        state.filtersConfig = filtersConfig || [];

        const totalItems = pagination?.total_records ?? 0;
        const totalPages = Math.max(1, pagination?.total_pages ?? 1);
        const pageSize = pagination?.limit ?? state.pagination.pageSize;
        const currentPage = pagination?.current_page ?? 1;

        state.pagination.totalItems = totalItems;
        state.pagination.totalPages = totalPages;
        state.pagination.pageSize = pageSize;
        state.pagination.currentPage = currentPage > totalPages ? 1 : currentPage;

        state.loading.getTriggersPaginated = false;
      })
      .addCase(getTriggersPaginated.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch triggers';
        state.loading.getTriggersPaginated = false;
      })

      .addCase(fetchTrigger.fulfilled, (state, action) => {
        const updated = action.payload as Trigger;
        const idx = state.triggers.findIndex((t) => t.trigger_id === updated.trigger_id);
        // Upsert: fetch-by-id must insert when the list slice is unpopulated
        // (e.g. opened from the Workspace), not only refresh an existing row.
        if (idx !== -1) state.triggers[idx] = updated;
        else state.triggers.push(updated);
      })

      .addCase(createTrigger.fulfilled, (state, action) => {
        state.triggers.push(action.payload as Trigger);
      })

      .addCase(updateTrigger.fulfilled, (state, action) => {
        const updated = action.payload as Trigger;
        const idx = state.triggers.findIndex((t) => t.trigger_id === updated.trigger_id);
        if (idx !== -1) state.triggers[idx] = updated;
      })

      .addCase(deleteTrigger.fulfilled, (state, action) => {
        const id = (action.payload as any)?.trigger_id;
        if (id) {
          state.triggers = state.triggers.filter((t) => String(t.trigger_id) !== String(id));
        }
      });
  },
});

export default triggersSlice.reducer;
